import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
// Components
import {
  Modal,
  Loader,
  ModalTitle,
  PhoneTextField,
  SelectAsync,
  Card,
  MobileTable,
  Table,
  ISelectOption,
  SMBSelect
} from '../../../components';
import { Close, Save, Subject, OtherHouses, DeleteForever, Build, Add } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Fade,
  Button,
  Grid,
  Box,
  useMediaQuery,
  Stack,
  FormControl,
  MenuItem,
  Typography,
  Divider,
  TextField
} from '@mui/material';
import {
  IDomainUser,
  ISupervisorNeighborhood,
  IResponse,
  ISupervisorActiveNeighborhood,
  ISupervisorDetail
} from '../../../models';
import { formatDate, phoneRegExp, formatDBFriendlyDateTime } from '../../../helpers';
import {
  updateSupervisor,
  createSupervisor,
  getDomainUsers,
  getSupervisorAvailableNeighborhoods,
  deleteSupervisorNeighborhood,
  createSupervisorNeighborhood,
  getSupervisorDetail
} from '../../../fetch';
import { DatePicker } from '@mui/x-date-pickers';

interface ISupervisorModal {
  open: boolean;
  onClose: () => void;
  supervisorId?: string | number;
  fetchSupervisors: () => void;
}

const Schema = Yup.object().shape({
  accountName: Yup.string().max(200, 'Max 200 characters').required('Required'),
  displayName: Yup.string().max(200, 'Max 200 characters').required('Required'),
  contactPhoneNumber: Yup.string().required('Required').matches(phoneRegExp, {
    excludeEmptyString: true,
    message: 'Invalid phone number'
  }),
  startsPerWeek: Yup.string().max(200, 'Max 200 characters'),
  breakCycle: Yup.string().max(200, 'Max 200 characters'),
  probationPeriodEnds: Yup.string().max(200, 'Max 200 characters')
});

export const SupervisorModal: FC<ISupervisorModal> = ({
  open,
  onClose,
  supervisorId,
  fetchSupervisors
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');
  const [accountOptions, setAccountOptions] = useState<ISelectOption[] | null>(null);
  const [isDeleting, setDeleting] = useState<{ [id: number]: boolean }>({});

  const {
    isLoading: isLoadingSupervisorAvailableNeighborhoods,
    data: supervisorNeighborhoods,
    refetch: refetchSupervisorNeighborhoods
  } = useQuery<ISupervisorActiveNeighborhood[], Error>(
    ['getSupervisorAvailableNeighborhoods', supervisorId],
    () => getSupervisorAvailableNeighborhoods(supervisorId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!supervisorId
    }
  );

  const {
    isLoading: isLoadingSupervisor,
    data: currentSupervisor,
    refetch: refetchSupervisor
  } = useQuery<ISupervisorDetail, Error>(
    ['getSupervisorDetail', supervisorId],
    () => getSupervisorDetail(supervisorId),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!supervisorId
    }
  );

  const handleDelete = async (neighborhoodId: number | string) => {
    const result = window.confirm('Are you sure you want to delete this neighborhood?');
    if (result) {
      setDeleting({ [neighborhoodId]: true });
      try {
        await deleteSupervisorNeighborhood(supervisorId, neighborhoodId);
        refetchSupervisorNeighborhoods();
        refetchSupervisor();
        enqueueSnackbar('Neighborhood removed!', {
          variant: 'success'
        });
      } catch (error) {
        enqueueSnackbar(error?.Detail ?? 'Error deleting neighborhood.', {
          variant: 'error'
        });
      } finally {
        setDeleting({ [neighborhoodId]: false });
      }
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'Phase',
        accessor: 'phase',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: ISupervisorActiveNeighborhood } };
        }) => {
          return (
            <>
              <LoadingButton
                loading={isDeleting[original.neighborhoodId]}
                loadingPosition="start"
                color="error"
                disabled={isDeleting[original.neighborhoodId]}
                startIcon={<DeleteForever />}
                onClick={() => {
                  handleDelete(original.neighborhoodId);
                }}
              >
                Delete
              </LoadingButton>
            </>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supervisorId, isDeleting]);

  const activeBuildsColumns = useMemo(() => {
    return [
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodDescription',
        sort: false
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        sort: false
      },
      {
        Header: 'Status',
        accessor: 'jobStatus',
        sort: false
      },
      {
        Header: 'Frame Date',
        accessor: 'frameDate',
        sort: false,
        Cell: props => <>{formatDate(props.cell.row.original.frameDate)}</>
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          accountName: currentSupervisor?.accountName ?? '',
          displayName: currentSupervisor?.accountDisplay ?? '',
          contactPhoneNumber: currentSupervisor?.contactPhoneNumber ?? '',
          startsPerWeek: currentSupervisor?.startsPerWeek ?? '',
          probationPeriodEnds: currentSupervisor?.probationPeriodEnds
            ? new Date(currentSupervisor?.probationPeriodEnds)
            : undefined,
          breakCycle: ''
        }}
        validationSchema={Schema}
        onSubmit={async (values, actions) => {
          try {
            if (currentSupervisor && currentSupervisor.supervisorId) {
              await updateSupervisor(currentSupervisor.supervisorId, {
                startsPerWeek: Number(values.startsPerWeek),
                probationPeriodEnds: formatDBFriendlyDateTime(values.probationPeriodEnds),
                contactPhoneNumber: values.contactPhoneNumber
              });
            } else {
              await createSupervisor({
                name: values.accountName,
                displayName: values.displayName,
                contactPhoneNumber: values.contactPhoneNumber,
                startsPerWeek: Number(values.startsPerWeek),
                probationPeriodEnds: formatDBFriendlyDateTime(values.probationPeriodEnds),
                breakCycle: Number(values.breakCycle)
              });
            }
            enqueueSnackbar(currentSupervisor ? 'Supervisor Updated!' : 'Supervisor Created!', {
              variant: 'success'
            });
            onClose();
            fetchSupervisors();
            actions.resetForm();
          } catch (error) {
            enqueueSnackbar(error?.Detail ?? 'Error saving supervisor.', {
              variant: 'error'
            });
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          handleSubmit,
          dirty,
          isValid,
          setFieldValue
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  resetForm();
                  onClose();
                }
              }}
              maxWidth="md"
            >
              {(isSubmitting || isLoadingSupervisor) && (
                <Loader type="overlay" position="centered" />
              )}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Box mb={1}>
                    <ModalTitle title={!!supervisorId ? `Edit Supervisor` : 'Add Supervisor'} />
                  </Box>
                  <Card title="Details" icon={<Subject />}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        {!currentSupervisor && (
                          <SelectAsync
                            name="accountName"
                            label="Active Directory User Account"
                            placeholder="Select"
                            required
                            disabled={!!supervisorId}
                            apiRequest={() =>
                              getDomainUsers({
                                sortBy: 'Name'
                              })
                            }
                            transformResponse={(response: IResponse<IDomainUser[]>) => {
                              return response.records.map(record => ({
                                label: record.displayName,
                                value: record.accountName
                              }));
                            }}
                            hasClear={!supervisorId}
                            onClear={() => {
                              setFieldValue('accountName', '');
                              setFieldValue('displayName', '');
                            }}
                            onChange={e => {
                              const selected = accountOptions?.find(
                                a => a.value === e.target.value
                              );
                              if (selected) {
                                setFieldValue('accountName', selected.value);
                                setFieldValue('displayName', selected.label);
                              }
                            }}
                            handleOptions={options => setAccountOptions(options)}
                          />
                        )}
                        {currentSupervisor && (
                          <TextField
                            fullWidth
                            name="accountName"
                            disabled={!!supervisorId}
                            label="Active Directory User Account"
                            value={currentSupervisor.accountName}
                            size="small"
                            autoComplete="nope"
                          />
                        )}
                      </Grid>
                      {currentSupervisor && (
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            disabled={!!supervisorId}
                            size="small"
                            autoComplete="nope"
                            label="Display Name"
                            name="displayName"
                            value={values.displayName}
                            required
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={supervisorId ? 4 : 6}>
                        <PhoneTextField
                          fullWidth
                          autoComplete="nope"
                          label="Contact Phone Number"
                          name="contactPhoneNumber"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          autoComplete="nope"
                          label="Starts Per Week"
                          name="startsPerWeek"
                          size="small"
                          value={values.startsPerWeek}
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                          onChange={e => {
                            const re = /^[0-9\b]+$/;
                            // if value is not blank, then test the regex
                            if (e.target.value === '' || re.test(e.target.value)) {
                              setFieldValue('startsPerWeek', e.target.value);
                            }
                          }}
                        />
                      </Grid>
                      {!currentSupervisor && (
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            autoComplete="nope"
                            label="Break Cycle"
                            name="breakCycle"
                            size="small"
                            value={values.breakCycle}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                            onChange={e => {
                              const re = /^[0-9\b]+$/;
                              // if value is not blank, then test the regex
                              if (e.target.value === '' || re.test(e.target.value)) {
                                setFieldValue('breakCycle', e.target.value);
                              }
                            }}
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined">
                          <DatePicker
                            label={'Probation Period Ends'}
                            value={values.probationPeriodEnds}
                            onChange={selectedDate => {
                              setFieldValue('probationPeriodEnds', selectedDate);
                            }}
                            slotProps={{
                              textField: {
                                size: 'small',
                                error: false
                              }
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Box marginTop="2rem">
                      <Button
                        disabled={!dirty || isSubmitting || !isValid}
                        type="submit"
                        startIcon={<Save />}
                        variant="contained"
                        color="primary"
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        startIcon={<Close />}
                        color="inherit"
                        onClick={() => {
                          if (!deepEqual(initialValues, values)) {
                            const result = window.confirm(
                              'You have unsaved changes, are you sure you want to exit?'
                            );
                            if (result) {
                              resetForm();
                              onClose();
                            } else {
                              return;
                            }
                          } else {
                            onClose();
                          }
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Card>
                  {!!supervisorId && (
                    <>
                      <Card title="Neighborhoods" icon={<OtherHouses />}>
                        <Stack
                          gap={1}
                          direction={{
                            xs: 'column',
                            sm: 'row'
                          }}
                          mb={1}
                        >
                          <SMBSelect
                            disabled={isLoadingSupervisorAvailableNeighborhoods}
                            label={
                              isLoadingSupervisorAvailableNeighborhoods
                                ? 'Loading...'
                                : 'Neighborhood'
                            }
                            value={selectedNeighborhoodId}
                            size="small"
                            onChange={e => setSelectedNeighborhoodId(e.target.value)}
                            handleClear={() => setSelectedNeighborhoodId('')}
                          >
                            {!isLoadingSupervisorAvailableNeighborhoods &&
                              supervisorNeighborhoods?.length === 0 && (
                                <MenuItem disabled>No neighborhoods available</MenuItem>
                              )}
                            {!isLoadingSupervisorAvailableNeighborhoods &&
                              supervisorNeighborhoods?.map((record, index) => {
                                return (
                                  <MenuItem key={`${index}`} value={record.neighborhoodId}>
                                    {record.name} Phase {record.phase}
                                  </MenuItem>
                                );
                              })}
                          </SMBSelect>
                          <Button
                            startIcon={<Add />}
                            sx={{ minWidth: '14rem' }}
                            onClick={async () => {
                              try {
                                await createSupervisorNeighborhood(
                                  supervisorId,
                                  selectedNeighborhoodId
                                );
                                refetchSupervisorNeighborhoods();
                                refetchSupervisor();
                                setSelectedNeighborhoodId('');
                                enqueueSnackbar('Neighborhood added!', {
                                  variant: 'success'
                                });
                              } catch (error) {
                                enqueueSnackbar(error?.Detail ?? 'Error creating neighborhood.', {
                                  variant: 'error'
                                });
                              }
                            }}
                            color="primary"
                            variant="contained"
                            disabled={!selectedNeighborhoodId}
                          >
                            Add Neighborhood
                          </Button>
                        </Stack>
                        <Divider />
                        {currentSupervisor?.neighborhoods?.length === 0 && (
                          <Box mt={1}>
                            <Typography align="center">No neighborhoods found.</Typography>
                          </Box>
                        )}
                        {currentSupervisor?.neighborhoods?.length > 0 && (
                          <Table
                            columns={columns}
                            data={currentSupervisor?.neighborhoods ?? []}
                            hidePagination
                            mobileProps={{
                              handleDelete: (val: ISupervisorNeighborhood) => {
                                handleDelete(val.neighborhoodId);
                              },
                              fields: columns
                                .filter(c => c.Header)
                                .map(c => ({
                                  name: c.Header,
                                  accessor: c.accessor,
                                  Cell: c?.Cell
                                }))
                            }}
                            ResponsiveComponent={isMobile ? MobileTable : undefined}
                          />
                        )}
                      </Card>
                      <Card title="Active Builds" icon={<Build />}>
                        {currentSupervisor?.activeBuilds?.length === 0 && (
                          <Typography align="center">No active builds.</Typography>
                        )}
                        {currentSupervisor?.activeBuilds?.length > 0 && (
                          <Table
                            columns={activeBuildsColumns}
                            data={currentSupervisor?.activeBuilds ?? []}
                            hidePagination
                            mobileProps={{
                              fields: columns
                                .filter(c => c.Header)
                                .map(c => ({
                                  name: c.Header,
                                  accessor: c.accessor,
                                  Cell: c?.Cell
                                }))
                            }}
                            ResponsiveComponent={isMobile ? MobileTable : undefined}
                          />
                        )}
                      </Card>
                    </>
                  )}
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
